import React from 'react';

import * as analytics from 'analytics/Analytics';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {GROWTH_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants, {
  COPYABLE_FOLDER_ITEMS,
  SHARE_LINK_ITEMS,
  STORABLE_ITEM_TYPES,
} from 'constants/ModelTypeConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import EntityAskButton, {ENTITY_ASK_ALLOWED_TABS} from 'llm/chat-library/ctas/EntityAskButton';
import InventoryAskButton from 'llm/chat-library/ctas/InventoryAskButton';
import StartChatWithEntryButton from 'llm/chat-library/ctas/StartChatWithEntryButton';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import ShareLinkModal, {SHARE_LINK_MODAL_KEY} from './growth/ShareLinkCreationModal';

interface Props {
  activeTabKey?: string | null;
  fileId: string;
  fileName: string;
  fileType: ModelTypeConstants;
  onDuplicate?: () => void;
  filePerms?: Pick<Permissions, 'writable' | 'readable'>;
}

function CopyButton({fileId, onDuplicate}: {fileId: string; onDuplicate: () => void}): JSX.Element {
  return (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy to...">
      <Button
        aria-label="Copy to..."
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="copy"
        onClick={onDuplicate}
        size={ButtonSize.MINI}
        disabled={!isFeatureEnabled({feature: StandardFeature.CREATE, item: {id: fileId}})}
        dataTestAttrs={{component: 'ShareBar', element: 'copyButton'}}
      />
    </Tooltip>
  );
}

export function ShareButton({
  className,
  fileId,
  fileName,
  buttonStyle,
  buttonSize,
  fileType,
  filePerms,
  iconOnly = false,
}: {
  className?: string;
  fileId: string;
  fileName: string;
  buttonStyle?: ButtonStyle;
  buttonSize: ButtonSize;
  fileType: ModelTypeConstants;
  filePerms?: Pick<Permissions, 'writable'>;
  iconOnly?: boolean;
}): JSX.Element | null {
  // Ideally we would use filePerms here to determine whether or not to show the share button,
  // but sometimes filePerms might be read-only but the user still has permission to share.
  // An example is when an entry is out for review, editing is locked down, but the user still has permission to share.
  // We will rely on the backend to set the correct permissions and sometimes show the share button even if the user doesn't have permission to share.
  const canShare = BenchlingConfig.ENABLE_SHARE_LINK && SHARE_LINK_ITEMS.includes(fileType);
  if (!canShare) {
    return null;
  }
  const userId = EntityStore.currentUser()?.id;
  if (!userId) {
    return null;
  }
  return (
    <Button
      aria-label="Share"
      buttonStyle={buttonStyle ?? ButtonStyle.SECONDARY}
      icon="share-v2"
      className={className}
      size={buttonSize}
      text={iconOnly ? null : 'Share'}
      onClick={() => {
        analytics.trackEvent(GROWTH_ANALYTICS_ACTIONS.OPENED_SHARE_LINK_CREATION_MODAL, {
          file_id: fileId,
          user_id: userId,
        });
        LegacyModalContainer.open(
          SHARE_LINK_MODAL_KEY,
          <ShareLinkModal fileId={fileId} fileName={fileName} userId={userId} />
        );
      }}
      dataTestAttrs={{component: 'ShareBar', element: 'shareButton'}}
    />
  );
}

function ShareBar(props: Props): JSX.Element {
  const {activeTabKey, fileId, fileName, fileType, onDuplicate, filePerms} = props;
  const askAgentType = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.ASK;
  const analysisAgentType = BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT
    ? AgentType.ASSISTANT
    : AgentType.ANALYSIS;
  const canCopy = COPYABLE_FOLDER_ITEMS.includes(fileType) && filePerms?.readable && onDuplicate;
  const shouldShowDataCaptureEditButton =
    fileType === ModelTypeConstants.ENTRY &&
    BenchlingConfig.LLM_ENABLE_COMPOSE_OVER_ENTRIES &&
    BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
  const shouldShowAssistantEditButton =
    fileType === ModelTypeConstants.ENTRY &&
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT_ENTRY_EDITING &&
    isAgentTypeEnabled(AgentType.ASSISTANT);
  const shouldShowAIButton = shouldShowDataCaptureEditButton || shouldShowAssistantEditButton;

  const isEditable = filePerms?.writable ?? false;
  const isInventoryItem = STORABLE_ITEM_TYPES.includes(fileType);

  const shouldShowAnalysisAiButton =
    fileType === ModelTypeConstants.ENTRY && isAgentTypeEnabled(analysisAgentType);

  const isOnAllowedTab = activeTabKey == null || ENTITY_ASK_ALLOWED_TABS.has(activeTabKey);
  const shouldShowEntityAskButton =
    fileType !== ModelTypeConstants.ENTRY && isAgentTypeEnabled(askAgentType) && isOnAllowedTab;

  return (
    <div className="buffer-tabs-shareBar" {...getTestProps({component: 'ShareBar', element: 'root'})}>
      {shouldShowEntityAskButton && <EntityAskButton fileId={fileId} />}
      {isInventoryItem && <InventoryAskButton itemId={fileId} />}
      <ShareButton
        className="u-mini-spacing-right"
        fileId={fileId}
        fileName={fileName}
        buttonSize={ButtonSize.MINI}
        fileType={fileType}
        filePerms={filePerms}
      />
      {canCopy && <CopyButton fileId={fileId} onDuplicate={onDuplicate} />}
      {(shouldShowAIButton || shouldShowAnalysisAiButton) && (
        <StartChatWithEntryButton
          entryId={fileId}
          showEditContents={shouldShowAIButton}
          isEditable={isEditable}
          showAnalyzeData={shouldShowAnalysisAiButton}
        />
      )}
    </div>
  );
}

export default ShareBar;
